// The ledger's shape, and the clean-up rules the old app applied whenever it loaded data
// (ported from v1's blank() and sanitize(), with one addition: fields it doesn't know are kept).
// Pure: no DOM, no network. Used by the app, the importer and the server jobs.

export type Ccy = 'SGD' | 'USD';
export const CCYS: Ccy[] = ['SGD', 'USD'];
export const okC = (c: unknown): c is Ccy => c === 'SGD' || c === 'USD';

// Records are kept as plain objects so unknown fields survive; these describe the known fields.
export type Rec = Record<string, unknown> & { id: string };
export interface Upcoming extends Record<string, unknown> { sym: string; ex: string; pay?: string; dps: number; ccy: Ccy; wht: number; note?: string }
export interface DayPoint extends Record<string, unknown> { d: string; sgd: number }
export interface HourPoint extends Record<string, unknown> { t: number }
export interface Fx { rate: number; date: string; src: string; at: number | null }

/** The fields the old app saved to the server (its DOC_FIELDS), in the same order. */
export const DOC_FIELDS = ['display', 'txns', 'income', 'stakes', 'cash', 'upcoming', 'dismissed', 'assets', 'inboxDone', 'inboxLast'] as const;
export const RECORD_LISTS = ['txns', 'income', 'stakes', 'cash'] as const;
export type RecordList = (typeof RECORD_LISTS)[number];

export interface Doc {
  display: Ccy;
  txns: Rec[];
  income: Rec[];
  stakes: Rec[];
  cash: Rec[];
  upcoming: Upcoming[];
  dismissed: string[];
  assets: Record<string, Record<string, unknown>>;
  inboxDone: unknown[];
  inboxLast: Record<string, unknown> | null;
  /** Fields the app doesn't know, kept for the round trip. */
  extra: Record<string, unknown>;
}

/** Everything one person has: what the old data file held, plus typed prices and a typed rate. */
export interface Ledger {
  doc: Doc;
  history: DayPoint[];
  intraday: HourPoint[];
  manualPrices: Record<string, Record<string, unknown>>;
  fxManual: Fx | null;
  /** Unknown fields from the top of an imported data file. */
  fileExtra: Record<string, unknown>;
}

export const blankDoc = (): Doc => ({
  display: 'SGD', txns: [], income: [], stakes: [], cash: [], upcoming: [], dismissed: [], assets: {}, inboxDone: [], inboxLast: null, extra: {},
});
export const blankLedger = (): Ledger => ({ doc: blankDoc(), history: [], intraday: [], manualPrices: {}, fxManual: null, fileExtra: {} });

export const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);

/** v1's history filter: a valid date and a value of zero or more. */
export const validDay = (h: unknown): h is DayPoint => isObj(h) && DATE_RE.test(String(h.d)) && +(h.sgd as number) >= 0;
export const validHour = (h: unknown): h is HourPoint => isObj(h) && typeof h.t === 'number' && isFinite(h.t);

/** The old app's own field names in a backup file (its in-memory state). */
export const BACKUP_KNOWN = ['v', 'display', 'currency', 'fx', 'txns', 'income', 'stakes', 'cash', 'upcoming', 'history', 'dismissed', 'inboxDone', 'inboxLast', 'prices', 'assets'];

/**
 * v1's sanitize(), applied to the ledger fields: the display currency (falling back to an old file's
 * `currency`), each record's currency defaulted the same way, and lists/objects checked. Unlike v1,
 * unknown fields are returned in `extra` rather than dropped.
 */
export function sanitizeDoc(d: unknown, knownTop: readonly string[] = DOC_FIELDS): Doc {
  const b = blankDoc();
  if (!isObj(d)) return b;
  const legacy: Ccy = okC(d.currency) ? d.currency : 'SGD';
  b.display = okC(d.display) ? d.display : legacy;
  const fix = (r: unknown) => Object.assign({}, r, { ccy: okC((r as Record<string, unknown>)?.ccy) ? (r as Record<string, unknown>).ccy : legacy }) as unknown as Rec;
  const arr = (x: unknown) => (Array.isArray(x) ? x : []);
  b.txns = arr(d.txns).map(fix);
  b.income = arr(d.income).map(fix);
  b.stakes = arr(d.stakes) as Rec[];
  b.cash = arr(d.cash).map(fix);
  b.upcoming = arr(d.upcoming) as Upcoming[];
  b.dismissed = arr(d.dismissed) as string[];
  b.inboxDone = arr(d.inboxDone);
  b.inboxLast = isObj(d.inboxLast) ? d.inboxLast : null;
  if (isObj(d.assets)) b.assets = d.assets as Doc['assets'];
  for (const k of Object.keys(d)) if (!knownTop.includes(k) && k !== 'currency') b.extra[k] = d[k];
  return b;
}

/** The ledger fields as the old app sent them to its server (unknown fields included). */
export function docJson(doc: Doc): Record<string, unknown> {
  const o: Record<string, unknown> = { ...doc.extra };
  for (const k of DOC_FIELDS) o[k] = doc[k];
  return o;
}

/** The key the old app gave a dividend event: feed-<SYM>-<ex date>. */
export const evId = (e: { sym?: unknown; ex?: unknown }) => 'feed-' + String(e.sym ?? '') + '-' + String(e.ex ?? '');
